/*
 * 馬布谷客製 v1.0（2026-10-05）搜尋強化 S1：zh-TW 打字下拉
 * Shopify 原生 predictive search 不支援 zh-TW（/search/suggest 回 HTTP 417），
 * 改用 Section Rendering API 取完整搜尋結果前 6 筆（sections/mabu-search-suggest.liquid）。
 * - 注音／拼音組字期間不送請求（compositionstart / compositionend）
 * - 300ms debounce、AbortController 取消舊請求、同一查詢記憶體快取
 * - 上下鍵選取、Enter 前往選中項（未選則照常送出表單）、Esc 關閉
 */
class MabuSearchSuggest extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-mabu-panel]');
    this.results = this.querySelector('[data-mabu-results]');
    this.popular = this.querySelector('[data-mabu-popular]');
    this.status = this.querySelector('[data-mabu-status]');
    if (!this.input || !this.panel || !this.results) return;

    this.sectionId = this.dataset.sectionId || 'mabu-search-suggest';
    this.searchUrl = this.dataset.searchUrl || '/search';
    this.debounceMs = Number(this.dataset.debounce) || 300;
    this.cache = new Map();
    this.options = [];
    this.activeIndex = -1;
    this.composing = false;
    this.controller = null;
    this.timer = null;

    this.bindEvents();
  }

  bindEvents() {
    // 一改字就取消舊選取，避免 Enter 跳到上一個查詢的結果
    this.input.addEventListener('compositionstart', () => {
      this.composing = true;
      this.setActive(-1);
    });
    this.input.addEventListener('compositionend', () => {
      this.composing = false;
      this.schedule();
    });
    this.input.addEventListener('input', (event) => {
      this.setActive(-1);
      if (this.composing || event.isComposing) return;
      this.schedule();
    });
    this.input.addEventListener('focus', () => {
      if (this.term() === '') this.showPopular();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));

    // 搜尋框的清除鈕由 search-form.js 處理，等它清空輸入後再回到熱門搜尋
    this.input.form?.addEventListener('reset', () => {
      setTimeout(() => {
        this.abortPending();
        this.clearResults();
        this.showPopular();
      });
    });

    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      // 開啟搜尋 modal 的那次點擊（summary）會冒泡到 document，不能把剛顯示的熱門搜尋關掉
      if (event.target.closest('summary')) return;
      this.close();
    });

    const details = this.closest('details');
    details?.addEventListener('toggle', () => {
      if (!details.open) {
        this.close();
      } else if (this.term() === '') {
        this.showPopular();
      }
    });
  }

  term() {
    return this.input.value.trim();
  }

  // 中文 1 字即可查；英數至少 2 字，避免 b、be 這類片段詞
  meetsMinLength(term) {
    if (/[㐀-鿿豈-﫿]/.test(term)) return term.length >= 1;
    return term.length >= 2;
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.update(), this.debounceMs);
  }

  async update() {
    const term = this.term();
    if (term === '') {
      this.abortPending();
      this.clearResults();
      this.showPopular();
      return;
    }
    if (!this.meetsMinLength(term)) {
      this.abortPending();
      this.clearResults();
      this.close();
      return;
    }

    this.hidePopular();
    const key = term.toLowerCase();
    if (this.cache.has(key)) {
      this.renderResults(this.cache.get(key));
      return;
    }

    this.abortPending();
    this.controller = new AbortController();
    this.setLoading(true);
    const url =
      `${this.searchUrl}?q=${encodeURIComponent(term)}` +
      `&type=product&options%5Bprefix%5D=last&section_id=${encodeURIComponent(this.sectionId)}`;

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`搜尋下拉請求失敗：HTTP ${response.status}`);
      const html = await response.text();
      const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('.mabu-suggest-response');
      if (!fragment) throw new Error('搜尋下拉回應缺少 .mabu-suggest-response');
      const markup = fragment.outerHTML;
      this.cache.set(key, markup);
      if (this.term().toLowerCase() !== key) return; // 使用者已改字，丟棄過期結果
      this.renderResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError();
    } finally {
      this.setLoading(false);
    }
  }

  abortPending() {
    clearTimeout(this.timer);
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  renderResults(markup) {
    this.results.innerHTML = markup;
    this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
    this.options.forEach((option, index) => {
      option.id = `${this.results.id}-opt-${index}`;
    });
    this.setActive(-1);

    const count = Number(this.results.querySelector('.mabu-suggest-response')?.dataset.count || 0);
    const term = this.term();
    this.announce(count > 0 ? `「${term}」找到 ${count} 筆結果，可用上下鍵選取` : `「${term}」沒有符合的商品`);
    this.openIfFocused();
  }

  renderError() {
    this.results.innerHTML =
      '<p class="mabu-suggest__empty">暫時無法載入建議，請按 Enter 查看完整搜尋結果。</p>';
    this.options = [];
    this.setActive(-1);
    this.announce('暫時無法載入建議，請按 Enter 查看完整搜尋結果');
    this.openIfFocused();
  }

  // 晚到的回應不能把使用者已關掉的面板重新打開
  openIfFocused() {
    if (this.contains(document.activeElement)) this.open();
  }

  clearResults() {
    this.results.innerHTML = '';
    this.options = [];
    this.setActive(-1);
  }

  // 熱門搜尋是一般連結（Tab 可達），listbox 沒有選項，所以 aria-expanded 維持 false
  showPopular() {
    if (!this.popular) return;
    this.popular.hidden = false;
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
  }

  hidePopular() {
    if (this.popular) this.popular.hidden = true;
  }

  setLoading(isLoading) {
    this.panel.classList.toggle('mabu-suggest--loading', isLoading);
    this.panel.setAttribute('aria-busy', String(isLoading));
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', String(this.options.length > 0));
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }

  setActive(index) {
    this.options.forEach((option, i) => {
      option.setAttribute('aria-selected', String(i === index));
    });
    this.activeIndex = index;
    const active = this.options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    switch (event.key) {
      case 'ArrowDown':
        if (this.options.length === 0) return;
        event.preventDefault();
        this.open();
        this.setActive(this.activeIndex + 1 >= this.options.length ? 0 : this.activeIndex + 1);
        break;
      case 'ArrowUp':
        if (this.options.length === 0) return;
        event.preventDefault();
        this.setActive(this.activeIndex - 1 < 0 ? this.options.length - 1 : this.activeIndex - 1);
        break;
      case 'Enter': {
        const active = this.options[this.activeIndex];
        if (!active || this.panel.hidden) return; // 未選取時照常送出表單
        event.preventDefault();
        window.location.href = active.href;
        break;
      }
      case 'Escape':
        if (!this.panel.hidden) this.close();
        break;
      default:
        break;
    }
  }
}

if (!customElements.get('mabu-search-suggest')) {
  customElements.define('mabu-search-suggest', MabuSearchSuggest);
}
